import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  PhoneAndroid as PhoneAndroidIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';

import { toastApiError } from '../../../utils/errorMessage';

import { type EmployeeDto } from 'src/api/api_data';
import { resetDeviceRegistration } from 'src/api/api_data';
import {
  getEmployeeDeviceInfo,
  upToDateLabel,
  type EmployeeDeviceInfoDto,
} from 'src/api/deviceVersions.api';
import { Label } from 'src/components/label';

interface EmployeeDeviceTabProps {
  employee: EmployeeDto;
  onDeviceReset: () => void;
}

export default function EmployeeDeviceTab({ employee, onDeviceReset }: EmployeeDeviceTabProps) {
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [deviceInfo, setDeviceInfo] = useState<EmployeeDeviceInfoDto | null>(null);
  const [deviceInfoLoading, setDeviceInfoLoading] = useState(true);
  const [deviceInfoError, setDeviceInfoError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDeviceInfoLoading(true);
    setDeviceInfoError(false);
    getEmployeeDeviceInfo(employee.id)
      .then((data) => {
        if (!cancelled) setDeviceInfo(data);
      })
      .catch(() => {
        if (!cancelled) setDeviceInfoError(true);
      })
      .finally(() => {
        if (!cancelled) setDeviceInfoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employee.id]);

  const handleResetDevice = async () => {
    try {
      setResetting(true);
      await resetDeviceRegistration(employee.id);
      toast.success('Cihaz kaydı başarıyla sıfırlandı');
      setResetDialogOpen(false);
      onDeviceReset();
    } catch (error: any) {
      toastApiError(error, 'Cihaz kaydı sıfırlanırken hata oluştu');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Cihaz Bilgileri
        </Typography>
        {employee.deviceId && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<RefreshIcon />}
            onClick={() => setResetDialogOpen(true)}
          >
            Cihaz Kaydını Sıfırla
          </Button>
        )}
      </Stack>

      {!employee.deviceId ? (
        <Alert severity="info">
          Bu çalışan henüz bir cihaz kaydı yapmamış.
        </Alert>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={3}>
              <Stack direction="row" spacing={2} alignItems="center">
                <PhoneAndroidIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    Cihaz Durumu
                  </Typography>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Kayıtlı"
                    color="success"
                    size="small"
                  />
                </Stack>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Cihaz ID
                </Typography>
                <Typography variant="body1" fontWeight={500} fontFamily="monospace">
                  {employee.deviceId}
                </Typography>
              </Stack>

              {deviceInfoLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Sürüm bilgisi yükleniyor…
                  </Typography>
                </Stack>
              ) : deviceInfoError ? (
                <Alert severity="warning">Sürüm bilgisi yüklenemedi.</Alert>
              ) : deviceInfo && deviceInfo.hasActiveDevice ? (
                <>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Uygulama Sürümü
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" fontWeight={500}>
                        {deviceInfo.appVersion || '—'}
                      </Typography>
                      {(() => {
                        const status = upToDateLabel(deviceInfo.upToDate);
                        return (
                          <Label variant="soft" color={status.color}>
                            {status.label}
                          </Label>
                        );
                      })()}
                    </Stack>
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      En Güncel Sürüm
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {deviceInfo.latestAppVersion || '—'}
                    </Typography>
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Sürüm Güncellenme
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {deviceInfo.appVersionUpdatedAt
                        ? new Date(deviceInfo.appVersionUpdatedAt).toLocaleString('tr-TR')
                        : '—'}
                    </Typography>
                  </Stack>
                </>
              ) : null}

              {employee.key && (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Cihaz Anahtarı
                  </Typography>
                  <Typography variant="body1" fontWeight={500} fontFamily="monospace">
                    {employee.key.substring(0, 20)}...
                  </Typography>
                </Stack>
              )}

              {employee.verified && (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Doğrulama Durumu
                  </Typography>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Doğrulanmış"
                    color="success"
                    size="small"
                  />
                </Stack>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Uyarı:</strong> Cihaz kaydını sıfırlamak, çalışanın mevcut cihaz bağlantısını
                kaldırır. Hesap engellenmişse (güvenlik ihlali nedeniyle) tekrar etkinleştirilir ve
                çalışan yeni bir cihazdan giriş yapabilir hale gelir.
              </Alert>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => !resetting && setResetDialogOpen(false)}
      >
        <DialogTitle>Cihaz Kaydını Sıfırla</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {employee.firstName} {employee.lastName} adlı çalışanın cihaz kaydını sıfırlamak
            istediğinizden emin misiniz?
            <br />
            <br />
            Bu işlem çalışanın mevcut cihaz bağlantısını kaldırır. Hesap "Engellendi" durumundaysa
            tekrar etkinleştirilir ve bekleyen şüpheli giriş kayıtları otomatik olarak çözümlenir.
            Çalışan tekrar giriş yaparak yeni cihaz kaydını oluşturabilir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)} disabled={resetting}>
            İptal
          </Button>
          <Button
            onClick={handleResetDevice}
            color="error"
            variant="contained"
            disabled={resetting}
          >
            {resetting ? 'Sıfırlanıyor...' : 'Sıfırla'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
